import React, { lazy, Suspense, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './theme/ThemeProvider';
import ErrorBoundary from './components/ErrorBoundary';

// Components (always loaded - used in layout shell)
import Layout from './components/Layout';
import NavigationBar from './components/NavigationBar';

// Blog pages are loaded eagerly: the build prerenders every post into a static
// snapshot, and a lazy chunk would flash the spinner over it before React
// re-rendered the same article (see scripts/prerender-blog.tsx).
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

/**
 * Wrap a dynamic import so a ChunkLoadError (the new deploy's asset
 * hashes don't match what the cached index.html asked for) triggers a
 * one-shot hard reload instead of hanging the Suspense fallback forever.
 * The sessionStorage guard prevents an infinite reload loop if the chunk
 * is legitimately broken.
 */
const lazyWithReload = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    factory().catch((err) => {
      const isChunkError =
        err?.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/i.test(err?.message ?? '') ||
        /Failed to fetch dynamically imported module/i.test(err?.message ?? '');
      if (isChunkError && !sessionStorage.getItem('chunk-reload')) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        // Return a never-resolving promise so Suspense stays up until reload.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );

// Clear the reload guard on a successful boot so a future deploy can trigger it again.
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => sessionStorage.removeItem('chunk-reload'));
}

// Lazy-loaded pages
const Home = lazyWithReload(() => import('./pages/Home'));
const SignIn = lazyWithReload(() => import('./pages/SignIn'));
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithReload(() => import('./pages/TermsOfService'));
const FAQ = lazyWithReload(() => import('./pages/FAQ'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const Recipes = lazyWithReload(() => import('./pages/Recipes'));
const RecipeDetail = lazyWithReload(() => import('./pages/RecipeDetail'));
const AddRecipe = lazyWithReload(() => import('./pages/AddRecipe'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const IngredientsList = lazyWithReload(() => import('./pages/IngredientsList'));
const IngredientForm = lazyWithReload(() => import('./components/Ingredient/IngredientForm'));
const IngredientDetail = lazyWithReload(() => import('./pages/IngredientDetail'));
const PromoLanding = lazyWithReload(() => import('./pages/PromoLanding'));
const GetApp = lazyWithReload(() => import('./pages/GetApp'));
const Posts = lazyWithReload(() => import('./pages/Posts'));
const Subscribe = lazyWithReload(() => import('./pages/Subscribe'));
const SubscribeSuccess = lazyWithReload(() => import('./pages/SubscribeSuccess'));
const SubscribeCancel = lazyWithReload(() => import('./pages/SubscribeCancel'));
const Account = lazyWithReload(() => import('./pages/Account'));
const AccountSignIn = lazyWithReload(() => import('./pages/AccountSignIn'));
const AdminHome = lazyWithReload(() => import('./pages/admin/AdminHome'));
const InfluencersList = lazyWithReload(() => import('./pages/admin/InfluencersList'));
const PromoCodesList = lazyWithReload(() => import('./pages/admin/PromoCodesList'));
const FeedbackList = lazyWithReload(() => import('./pages/admin/FeedbackList'));
const AdminRoute = lazyWithReload(() => import('./components/AdminRoute'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const PageSpinner: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <CircularProgress sx={{ color: 'var(--accent)' }} size={48} />
  </Box>
);

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Instant, not smooth: html has `scroll-behavior: smooth`, and a smooth
    // scroll started in the same frame as the new route's layout shift gets
    // cancelled, leaving a blog post opened from the bottom of the index
    // stuck mid-page.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return null;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: 'var(--accent)' }} size={48} />
      </Box>
    );
  }

  return user ? <>{children}</> : <Navigate to="/signin" />;
};

// App Shell with Sidebar (used by signed-in & admin pages)
const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: 'var(--bg)' }}>
      <NavigationBar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          pt: { xs: '72px', md: 3 },
          minWidth: 0,
          overflow: 'auto',
          backgroundColor: 'var(--bg)',
          color: 'var(--ink)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const AppContent: React.FC = () => {
  return (
    <>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
            <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
            <Route path="/faq" element={<Layout><FAQ /></Layout>} />
            <Route path="/blog" element={<Layout><Blog /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />

            {/* Link-in-bio landing (no site chrome - built for in-app browsers) */}
            <Route path="/download" element={<GetApp />} />
            {/* Where the Instagram/Facebook RECIPES DM lands: the latest carousels' recipes */}
            <Route path="/posts" element={<Posts />} />

            {/* User account (distinct from admin /signin) */}
            <Route path="/account/signin" element={<AccountSignIn />} />
            <Route path="/account" element={<Account />} />

            {/* Influencer / web-to-app subscription funnel */}
            <Route path="/r/:code" element={<PromoLanding />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/subscribe/success" element={<SubscribeSuccess />} />
            <Route path="/subscribe/cancel" element={<SubscribeCancel />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><AdminLayout><Dashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/recipes" element={<ProtectedRoute><AdminLayout><Recipes /></AdminLayout></ProtectedRoute>} />
            <Route path="/recipes/new" element={<ProtectedRoute><AdminLayout><AddRecipe /></AdminLayout></ProtectedRoute>} />
            <Route path="/recipes/:id" element={<ProtectedRoute><AdminLayout><RecipeDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><AdminLayout><Settings /></AdminLayout></ProtectedRoute>} />
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminLayout><AdminHome /></AdminLayout></AdminRoute>} />
            <Route path="/admin/influencers" element={<AdminRoute><AdminLayout><InfluencersList /></AdminLayout></AdminRoute>} />
            <Route path="/admin/promo-codes" element={<AdminRoute><AdminLayout><PromoCodesList /></AdminLayout></AdminRoute>} />
            <Route path="/admin/feedback" element={<AdminRoute><AdminLayout><FeedbackList /></AdminLayout></AdminRoute>} />

            <Route
              path="/ingredients/*"
              element={
                <ProtectedRoute>
                  <AdminLayout>
                    <Routes>
                      <Route index element={<IngredientsList />} />
                      <Route path="new" element={<IngredientForm />} />
                      <Route path=":id" element={<IngredientDetail />} />
                      <Route path=":id/edit" element={<IngredientForm />} />
                      <Route path="*" element={<Navigate to="/ingredients" replace />} />
                    </Routes>
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Suspense>
      </Router>
    </>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <ThemeProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </ThemeProvider>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
